/**
 * BandwagonScreen1.tsx - Screen with social influence and required reasoning
 * Last updated: 2025-11-11
 * Updated by: Copilot
 */

import React, { useEffect, useState } from "react";
import { Icon } from "@blueprintjs/core";
import { ScreenProps } from "../../SequenceController";
import { ScreenConfig } from "../Sequences";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors, commonStyles } from "../../../../components/colors";
import movieNight from "./assets/movie_night.png";
import streamingImage from "./assets/streaming_option.png";
import cinemaImage from "./assets/cinema_option.png";

// Define types directly in this file
export type BandwagonChoice = 'streaming' | 'cinema';

export interface SelectionData {
    choice: BandwagonChoice;
    millisecondsSinceFirstRender: number;
    defaultOption: BandwagonChoice | null;
}

export interface BandwagonScreenResult {
    selections: SelectionData[];
    reason?: string;
}

export const BandwagonScreenInitialResult: BandwagonScreenResult = {
    selections: [],
    reason: ""
};

function FilmOptions({ selected, setSelected, isDefault }: {
    selected: BandwagonChoice | null,
    setSelected: (choice: BandwagonChoice) => void,
    isDefault: (choice: BandwagonChoice) => boolean
}) {
    const { t } = useLanguage();

    const options = [
        {
            value: 'streaming',
            title: t('experiments', 'bandwagon', 'screens', 'task', 'options', 'streaming'),
            image: streamingImage,
            features: t('experiments', 'bandwagon', 'screens', 'task', 'features', 'streaming', { returnObjects: true }) as string[]
        },
        {
            value: 'cinema',
            title: t('experiments', 'bandwagon', 'screens', 'task', 'options', 'cinema'),
            image: cinemaImage,
            features: t('experiments', 'bandwagon', 'screens', 'task', 'features', 'cinema', { returnObjects: true }) as string[]
        }
    ];

    return (
        <div style={{
            width: '100%',
            display: "flex",
            flexDirection: "column",
            gap: 24
        }}>
            <div style={{
                display: "flex",
                gap: 24,
                flexDirection: window.innerWidth < 600 ? "column" : "row"
            }}>
                {options.map((option) => (
                    <div
                        key={option.value}
                        style={selected === option.value
                            ? commonStyles.optionCard.selected
                            : commonStyles.optionCard.default}
                        onClick={() => setSelected(option.value as BandwagonChoice)}
                    >
                        {selected === option.value && (
                            <div style={commonStyles.selectionCheck}>
                                <Icon icon="tick" size={16} />
                            </div>
                        )}
                        <div style={{
                            marginBottom: 20,
                            borderRadius: 12,
                            overflow: "hidden",
                            boxShadow: "0 6px 20px 0 rgba(0,0,0,0.09)"
                        }}>
                            <img
                                src={option.image}
                                alt={option.title}
                                style={{
                                    width: "100%",
                                    height: 180,
                                    objectFit: "cover",
                                    display: "block"
                                }}
                            />
                        </div>
                        <h3 style={{
                            color: colors.brillantBlue,
                            marginBottom: 20,
                            fontSize: 20,
                            fontWeight: 600
                        }}>
                            {option.title}
                        </h3>
                        <ul style={{
                            listStyleType: "none",
                            padding: 0,
                            margin: 0,
                            fontSize: 17,
                            lineHeight: 1.7
                        }}>
                            {option.features.map((feature, idx) => (
                                <li key={idx} style={{
                                    marginBottom: 12,
                                    paddingLeft: 28,
                                    position: "relative"
                                }}>
                                    <Icon
                                        icon="dot"
                                        style={{
                                            position: "absolute",
                                            left: 0,
                                            top: 6,
                                            color: colors.lichtBlau
                                        }}
                                    />
                                    {feature}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function BandwagonScreen1(props: ScreenProps) {
    const { t, language } = useLanguage();
    const defaultOption: BandwagonChoice | null = null;
    const [firstRenderTimestamp, setFirstRenderTimestamp] = useState(0);
    const [selected, setSelected] = useState<BandwagonChoice | null>(null);

    // Reason handling
    const [reason, setReason] = useState("");
    const [reasonTouched, setReasonTouched] = useState(false);
    const [reasonError, setReasonError] = useState<string>("");

    const { setCanContinue, setResult } = props;

    useEffect(() => {
        setFirstRenderTimestamp(Date.now());
        setCanContinue(false);
        setResult(() => ({ ...BandwagonScreenInitialResult }));
        setReason("");
        setReasonTouched(false);
        setReasonError("");
    }, [setCanContinue, setResult]);

    useEffect(() => {
        const reasonOk = reason.trim().length > 0;
        if (!reasonOk && reasonTouched) {
            setReasonError(
                t('experiments', 'bandwagon', 'screens', 'task', 'reasonRequired') ||
                (language === "de" ? "(erforderlich)" : "(required)")
            );
        } else {
            setReasonError("");
        }
        const canContinue = selected !== null && reasonOk;
        setCanContinue(canContinue);

        if (canContinue && selected !== null) {
            setResult((prev: BandwagonScreenResult) => ({
                selections: [{
                    choice: selected,
                    millisecondsSinceFirstRender: Date.now() - firstRenderTimestamp,
                    defaultOption
                }],
                reason: reason.trim()
            }));
        } else {
            setResult((prev: BandwagonScreenResult) => ({
                ...prev,
                selections: selected !== null
                    ? [{
                        choice: selected,
                        millisecondsSinceFirstRender: Date.now() - firstRenderTimestamp,
                        defaultOption
                    }]
                    : [],
                reason: reason
            }));
        }
    }, [selected, reason, reasonTouched, setCanContinue, setResult, firstRenderTimestamp, t, language, defaultOption]);

    const isDefault = (choice: BandwagonChoice) => {
        if (defaultOption === null) return false;
        return choice === defaultOption;
    };

    return (
        <div className="experiment-container" style={{
            maxWidth: 1250,
            margin: "0 auto",
            padding: "24px 20px 44px",
            backgroundColor: colors.backgroundLight
        }}>
            <div style={{
                background: colors.white,
                borderRadius: 14,
                boxShadow: "0 2px 12px rgba(0,0,0,0.09)",
                padding: "24px 40px 28px",
                marginBottom: 28
            }}>
                <h2 style={{
                    color: colors.brillantBlue,
                    margin: "0 0 24px 0",
                    fontSize: 28,
                    fontWeight: 700,
                    textAlign: "center"
                }}>
                    {t('experiments', 'bandwagon', 'screens', 'task', 'filmEvening')}
                </h2>
                <div style={{
                    fontSize: 17,
                    lineHeight: 1.7,
                    color: colors.textMedium
                }}>
                    <p style={{ marginTop: 0 }}>
                        {t('experiments', 'bandwagon', 'screens', 'task', 'neutralInstructions')}
                    </p>
                </div>
                <div style={{ textAlign: "center", marginBottom: 0 }}>
                    <img
                        src={movieNight}
                        alt={t('experiments', 'bandwagon', 'screens', 'task', 'filmEvening')}
                        style={{
                            maxWidth: 480,
                            width: "100%",
                            height: "auto",
                            borderRadius: 16,
                            boxShadow: "0 6px 20px 0 rgba(0,0,0,0.09)"
                        }}
                    />
                </div>
            </div>
            <div style={{
                backgroundColor: colors.ocker,
                border: `1px solid ${colors.ockerLight}`,
                padding: "14px 20px",
                borderRadius: 8,
                marginBottom: 22,
                fontSize: 16,
                fontWeight: 500,
                color: colors.white
            }}>
                <span style={{ display: "flex", alignItems: "center" }}>
                    <Icon icon="people" style={{ marginRight: 10 }} />
                    {t('experiments', 'bandwagon', 'screens', 'task', 'socialInfluence')}
                </span>
            </div>
            <h3 style={{
                color: colors.brillantBlue,
                textAlign: "center",
                marginBottom: 20,
                fontSize: 24,
                fontWeight: 600
            }}>
                {t('experiments', 'bandwagon', 'screens', 'task', 'question')}
            </h3>
            <FilmOptions selected={selected} setSelected={setSelected} isDefault={isDefault} />
            {/* Reason field */}
            <div style={{ marginTop: 38, maxWidth: 670, marginLeft: "auto", marginRight: "auto" }}>
                <label style={{
                    display: "block",
                    fontWeight: 600,
                    color: colors.textDark,
                    marginBottom: 8
                }}>
                    {t('experiments', 'bandwagon', 'screens', 'task', 'reasonLabel') ||
                        (language === "de" ? "Bitte begründe deine Wahl:" : "Please explain your choice:")}{" "}
                    <span style={{ fontWeight: 500, color: colors.textMedium }}>
                        {t('experiments', 'bandwagon', 'screens', 'task', 'reasonRequired') ||
                            (language === "de" ? "(erforderlich)" : "(required)")}
                    </span>
                </label>
                <textarea
                    aria-label={t('experiments', 'bandwagon', 'screens', 'task', 'reasonLabel') ||
                        (language === "de" ? "Begründung für die Wahl" : "Reason for choice")}
                    placeholder={t('experiments', 'bandwagon', 'screens', 'task', 'reasonPlaceholder') ||
                        (language === "de" ? "Bitte gib eine kurze Begründung an." : "Please enter a brief explanation.")}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    onBlur={() => setReasonTouched(true)}
                    rows={4}
                    style={{
                        width: "100%",
                        maxWidth: 640,
                        minHeight: 96,
                        padding: 12,
                        fontSize: 15,
                        borderRadius: 8,
                        border: "1px solid #e1e6ea",
                        resize: "vertical",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.03)"
                    }}
                />
                {reasonError && <div style={{ color: "red", marginTop: 8 }}>{reasonError}</div>}
            </div>
        </div>
    );
}

export const BandwagonScreen1Config: ScreenConfig = {
    component: BandwagonScreen1,
    defaultResult: BandwagonScreenInitialResult,
    canContinueDefault: false
};